import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { GatewayApiClient } from '@evidentid/gateway-api-client';
import {
    ActivityHistoryActionableTags,
    ActivityHistoryEntry,
    ActivityHistoryQuery,
} from '@evidentid/tprm-portal-lib/models/activity-history';
const LIMIT = 25;

export interface EntityEventsRequirement {
    gatewayClient: GatewayApiClient;
}

export interface ActivityHistoryStatus {
    loading: boolean;
    finished: boolean;
    list: ActivityHistoryEntry[];
    filters: ActivityHistoryQuery;
    actionableTags: ActivityHistoryActionableTags;
}

interface EntityEventsState {
    activityHistory: Record<string, ActivityHistoryStatus>;
}

function createEmptyStatus(): ActivityHistoryStatus {
    return {
        loading: false,
        finished: false,
        list: [],
        filters: {},
        actionableTags: { clientTags: [], systemTags: [] },
    };
}

function statusKey(rpName: string, entityId: string): string {
    return `${rpName}:${entityId}`;
}

// Actions reach the Application through the $app Pinia plugin; typed here once.
function appOf(store: { $app: any }): EntityEventsRequirement {
    return store.$app;
}

export const useEntityEventsStore = defineStore('entityEvents', {
    state: (): EntityEventsState => ({
        activityHistory: {},
    }),
    actions: {
        startLoadingActivityHistory(payload: { rpName: string, entityId: string, reset?: boolean }) {
            const key = statusKey(payload.rpName, payload.entityId);
            const current = this.activityHistory[key] || createEmptyStatus();
            this.activityHistory = {
                ...this.activityHistory,
                [key]: {
                    ...current,
                    loading: true,
                    ...(payload.reset ? { list: [], finished: false } : {}),
                },
            };
        },

        finishLoadingActivityHistory(payload: {
            rpName: string;
            entityId: string;
            records: ActivityHistoryEntry[];
            finished: boolean;
            append: boolean;
        }) {
            const key = statusKey(payload.rpName, payload.entityId);
            const current = this.activityHistory[key] || createEmptyStatus();
            this.activityHistory = {
                ...this.activityHistory,
                [key]: {
                    ...current,
                    loading: false,
                    finished: payload.finished,
                    list: payload.append ? [ ...current.list, ...payload.records ] : payload.records,
                },
            };
        },

        failLoadingActivityHistory(payload: { rpName: string, entityId: string }) {
            const key = statusKey(payload.rpName, payload.entityId);
            const current = this.activityHistory[key] || createEmptyStatus();
            this.activityHistory = { ...this.activityHistory, [key]: { ...current, loading: false } };
        },

        setActivityHistoryFilters(payload: { rpName: string, entityId: string, filters: ActivityHistoryQuery }) {
            const key = statusKey(payload.rpName, payload.entityId);
            const current = this.activityHistory[key] || createEmptyStatus();
            this.activityHistory = { ...this.activityHistory, [key]: { ...current, filters: payload.filters } };
        },

        setActivityHistoryActionableTags(payload: {
            rpName: string;
            entityId: string;
            actionableTags: ActivityHistoryActionableTags;
        }) {
            const key = statusKey(payload.rpName, payload.entityId);
            const current = this.activityHistory[key] || createEmptyStatus();
            this.activityHistory = {
                ...this.activityHistory,
                [key]: { ...current, actionableTags: payload.actionableTags },
            };
        },

        clearActivityHistory(payload: { rpName: string, entityId: string }) {
            this.activityHistory = omit(this.activityHistory, statusKey(payload.rpName, payload.entityId));
        },

        async loadActivityHistoryTags(payload: { rpName: string, entityId: string }) {
            const { rpName, entityId } = payload;
            try {
                const actionableTags = await appOf(this).gatewayClient.tprmUi()
                    .getActivityHistoryActionableTags(rpName, entityId);
                this.setActivityHistoryActionableTags({ rpName, entityId, actionableTags });
            } catch (error) {
                console.warn('load activity history tags error', error);
            }
        },

        async loadActivityHistory(payload: {
            rpName: string;
            entityId: string;
            filters?: ActivityHistoryQuery;
        }) {
            const { rpName, entityId, filters = {} } = payload;
            const key = statusKey(rpName, entityId);
            if ((this.activityHistory[key] || createEmptyStatus()).loading) {
                return;
            }
            this.setActivityHistoryFilters({ rpName, entityId, filters });
            this.startLoadingActivityHistory({ rpName, entityId, reset: true });
            this.loadActivityHistoryTags({ rpName, entityId });
            try {
                const result = await appOf(this).gatewayClient.tprmUi().getActivityHistory(rpName, entityId, {
                    skip: 0,
                    limit: LIMIT,
                    ...filters,
                });
                this.finishLoadingActivityHistory({
                    rpName,
                    entityId,
                    records: result.records,
                    finished: result.records.length < LIMIT,
                    append: false,
                });
            } catch (error) {
                console.warn('load activity history error', error);
                this.failLoadingActivityHistory({ rpName, entityId });
            }
        },

        /** Reloads the first page with the filters already applied, if the history has been loaded at all. */
        async reloadActivityHistory(payload: { rpName: string, entityId: string }) {
            const current = this.activityHistory[statusKey(payload.rpName, payload.entityId)];
            if (!current) {
                return;
            }
            await this.loadActivityHistory({ ...payload, filters: current.filters });
        },

        async loadNextActivityHistory(payload: { rpName: string, entityId: string }) {
            const { rpName, entityId } = payload;
            const key = statusKey(rpName, entityId);
            const current = this.activityHistory[key] || createEmptyStatus();
            if (current.loading || current.finished) {
                return;
            }
            this.startLoadingActivityHistory({ rpName, entityId });
            try {
                const result = await appOf(this).gatewayClient.tprmUi().getActivityHistory(rpName, entityId, {
                    skip: current.list.length,
                    limit: LIMIT,
                    ...current.filters,
                });
                this.finishLoadingActivityHistory({
                    rpName,
                    entityId,
                    records: result.records,
                    finished: result.records.length < LIMIT,
                    append: true,
                });
            } catch (error) {
                console.warn('load next activity history error', error);
                this.failLoadingActivityHistory({ rpName, entityId });
            }
        },

    },
});
